import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "react-toastify";

import { SecondaryButton } from "@/components/common";

import { CannabinoidStorePicker } from "./CannabinoidStorePicker";
import { CheckoutAddressWizardDialog } from "./CheckoutAddressWizardDialog";
import { CheckoutPaymentOverlay } from "./CheckoutPaymentOverlay";
import { MarketplaceConfigError } from "./MarketplaceConfigError";
import { MarketplacePaymentIframeOverlay } from "./MarketplacePaymentIframeOverlay";
import { MarketplaceWaitingOverlay } from "./MarketplaceWaitingOverlay";
import { PixPaymentDialog } from "./PixPaymentDialog";
import { CheckoutReviewDialog } from "./CheckoutReviewDialog";
import { DispensaryConsentDialog } from "./DispensaryConsentDialog";
import { WooCheckoutTriggerButton } from "./WooCheckoutTriggerButton";
import { WooLoginPromptDialog } from "./WooLoginPromptDialog";
import { WooOrdersModal, type OrdersTab } from "./WooOrdersModal";
import { WooOrdersTriggerButton } from "./WooOrdersTriggerButton";
import { WooPaymentSuccessDialog } from "./WooPaymentSuccessDialog";
import { WooProductGrid } from "./WooProductGrid";
import { isStoreApiReady } from "../constants/organizationStoreRegistry";
import { useWooCommerceCart } from "../hooks/useWooCommerceCart";
import { useWooCommerceCheckout } from "../hooks/useWooCommerceCheckout";
import { useWooCommerceCustomer } from "../hooks/useWooCommerceCustomer";
import { useWooCommerceOrdersAllStores } from "../hooks/useWooCommerceOrdersAllStores";
import { woocommerceStoreService } from "../services/woocommerceStoreService";
import { useWooCommercePaymentMonitor } from "../hooks/useWooCommercePaymentMonitor";
import { useWooCommerceProducts } from "../hooks/useWooCommerceProducts";
import { useWooCommerceStores } from "../hooks/useWooCommerceStores";
import type {
  MarketplaceAddressOption,
  MarketplaceCheckoutDraft,
  WooCustomer,
  WooOrder,
  WooStoreConfig,
} from "../types";
import {
  buildAddressOptionsForCheckout,
  buildDefaultCheckoutAddresses,
  formatAddressSummary,
  isCheckoutAddressComplete,
} from "../utils/addressHelpers";
import { pharmaConsentService } from "../../services/pharmaConsentService";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";

interface CannabinoidStoreMarketplaceProps {
  userEmail: string;
  patientId?: string;
  userPatient?: unknown;
  userName?: string;
  inline?: boolean;
  hideHeader?: boolean;
  /** false = bloqueia abrir lojas; pedidos e pagamentos permanecem disponíveis. */
  canBrowseStores?: boolean;
  documentsPendingCount?: number;
  onHeaderActionsChange?: (actions: ReactNode | null) => void;
}

export function CannabinoidStoreMarketplace({
  userEmail,
  patientId = "",
  userPatient,
  userName,
  inline = true,
  hideHeader = false,
  canBrowseStores = true,
  documentsPendingCount = 0,
  onHeaderActionsChange,
}: CannabinoidStoreMarketplaceProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    stores,
    activeStores,
    isConfigured,
    loading: storesLoading,
    error: storesError,
    configError: storesConfigError,
  } = useWooCommerceStores();
  const [selectedStore, setSelectedStore] = useState<WooStoreConfig | null>(null);
  const [consentedInstitutionIds, setConsentedInstitutionIds] = useState<Set<string>>(
    new Set(),
  );
  const [consentDialogStore, setConsentDialogStore] = useState<WooStoreConfig | null>(
    null,
  );
  const [checkoutAddressOptions, setCheckoutAddressOptions] = useState<
    MarketplaceAddressOption[]
  >([]);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [ordersModalTab, setOrdersModalTab] = useState<OrdersTab>("unpaid");
  const [checkoutDraft, setCheckoutDraft] = useState<MarketplaceCheckoutDraft | null>(null);
  const [showCheckoutReview, setShowCheckoutReview] = useState(false);
  const [showAddressWizard, setShowAddressWizard] = useState(false);
  const [addressWizardStep, setAddressWizardStep] = useState<1 | 2>(1);
  const [addressWizardFocus, setAddressWizardFocus] = useState<
    "shipping" | "billing" | "default"
  >("default");
  const [checkoutPreparing, setCheckoutPreparing] = useState(false);
  const [paymentOverlayOpen, setPaymentOverlayOpen] = useState(false);

  const {
    customer,
    status: customerStatus,
    loading: customerLoading,
    error: customerError,
    resolveCustomer,
    linkAccount,
    cancelLinkAccount,
    isLinkingAccount,
    refreshCustomer,
    resetCustomer,
  } = useWooCommerceCustomer(selectedStore, userEmail);

  const {
    pendingOrders,
    orders,
    loadAllOrders,
    refreshAllOrders,
    cancelOrder,
    loading: ordersLoading,
    error: ordersError,
  } = useWooCommerceOrdersAllStores(activeStores, userEmail);

  const resolveOrderStore = useCallback(
    (order: WooOrder) => stores.find((store) => store.id === order.storeId) ?? null,
    [stores],
  );

  const {
    payOrder,
    monitoringOrderId,
    successOrder,
    clearSuccessOrder,
    pixPayment,
    closePixPayment,
    embeddedPayment,
    closeEmbeddedPayment,
  } =
    useWooCommercePaymentMonitor({
      store: selectedStore,
      resolveStore: resolveOrderStore,
      onOrdersRefresh: async () => {
        await refreshAllOrders();
      },
    });

  const {
    products,
    searchTerm,
    setSearchTerm,
    loadProducts,
    loading: productsLoading,
    error: productsError,
    reset: resetProducts,
  } = useWooCommerceProducts(selectedStore);

  const {
    getCartItems,
    addToCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getTotalItemsAllStores,
  } = useWooCommerceCart();

  const { checkoutStore: runCheckout, loading: checkoutLoading, error: checkoutError } =
    useWooCommerceCheckout();

  const storeId = selectedStore?.id ?? "";
  const totalItems = getTotalItemsAllStores();

  const activeCheckoutStore = checkoutDraft?.store ?? selectedStore;
  const activeCheckoutCustomer = checkoutDraft?.customer ?? customer;

  const canBrowseStoreCatalog = customerStatus === "found";
  const showLoginPrompt =
    customerStatus === "login_required" && !isLinkingAccount;

  useEffect(() => {
    if (!selectedStore) return;
    resetCustomer();
    resetProducts();
    void resolveCustomer();
  }, [selectedStore?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userEmail.trim() || activeStores.length === 0) return;
    void loadAllOrders();
  }, [userEmail, activeStores.length, loadAllOrders]);

  useEffect(() => {
    if (!selectedStore) return;
    if (customerStatus === "found" && customer) {
      void loadProducts();
    }
  }, [customerStatus, selectedStore?.id, customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customerError) toast.error(customerError);
    if (checkoutError) toast.error(checkoutError);
    if (ordersError) toast.error(ordersError);
  }, [customerError, checkoutError, ordersError]);

  useEffect(() => {
    if (!patientId.trim()) return;

    let cancelled = false;

    (async () => {
      try {
        const consents =
          await pharmaConsentService.getActiveOrganizationConsents(patientId);
        if (!cancelled) {
          setConsentedInstitutionIds(
            new Set(consents.map((c) => c.institutionId).filter(Boolean)),
          );
        }
      } catch {
        // silencioso — badge de consentimento pode ficar impreciso
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const blockStoreBrowsing = useCallback(() => {
    toast.info(
      "Envie todos os documentos do tratamento canabinoide para acessar os dispensadores.",
    );
  }, []);

  const enterStore = useCallback((store: WooStoreConfig) => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      return;
    }
    setSelectedStore(store);
  }, [canBrowseStores, blockStoreBrowsing]);

  const handleSelectStore = useCallback(
    async (store: WooStoreConfig) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      if (!isStoreApiReady(store)) return;

      const institutionId = store.institutionId;
      if (!institutionId || !patientId.trim()) {
        enterStore(store);
        return;
      }

      if (consentedInstitutionIds.has(institutionId)) {
        enterStore(store);
        return;
      }

      try {
        const hasConsent = await pharmaConsentService.hasConsentForInstitution(
          patientId,
          institutionId,
        );
        if (hasConsent) {
          setConsentedInstitutionIds((prev) => new Set(prev).add(institutionId));
          enterStore(store);
          return;
        }
      } catch {
        toast.error("Não foi possível verificar a autorização.");
        return;
      }

      setConsentDialogStore(store);
    },
    [patientId, consentedInstitutionIds, enterStore, canBrowseStores, blockStoreBrowsing],
  );

  const handleConsentGranted = useCallback(() => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      setConsentDialogStore(null);
      return;
    }
    if (!consentDialogStore?.institutionId) return;
    setConsentedInstitutionIds((prev) =>
      new Set(prev).add(consentDialogStore.institutionId!),
    );
    const storeToEnter = consentDialogStore;
    setConsentDialogStore(null);
    enterStore(storeToEnter);
  }, [consentDialogStore, enterStore, canBrowseStores, blockStoreBrowsing]);

  const handleAddToCart = useCallback(
    (product: Parameters<typeof addToCart>[1]) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      if (!selectedStore) return;
      addToCart(selectedStore.id, product);
    },
    [canBrowseStores, blockStoreBrowsing, selectedStore, addToCart],
  );

  const resetCheckoutContext = useCallback(() => {
    setCheckoutDraft(null);
    setShowCheckoutReview(false);
    setShowAddressWizard(false);
    setAddressWizardStep(1);
  }, []);

  useEffect(() => {
    if (canBrowseStores) return;
    setSelectedStore(null);
    setConsentDialogStore(null);
    resetCheckoutContext();
    resetCustomer();
  }, [canBrowseStores, resetCheckoutContext, resetCustomer]);

  const executeCheckout = useCallback(async () => {
    if (!checkoutDraft?.addresses || !activeCheckoutStore || !activeCheckoutCustomer) {
      toast.error("Informe os endereços de entrega e cobrança antes de continuar.");
      return;
    }

    const { addresses, cartItems, store, customer: draftCustomer } = checkoutDraft;

    if (!isCheckoutAddressComplete(addresses.shipping)) {
      toast.error("Informe CPF, telefone e endereço de entrega completos.");
      return;
    }

    const billingSameAsShipping =
      formatAddressSummary(addresses.shipping) ===
      formatAddressSummary(addresses.billing);

    if (!billingSameAsShipping && !isCheckoutAddressComplete(addresses.billing)) {
      toast.error("Endereço de cobrança incompleto (CPF e telefone obrigatórios).");
      return;
    }

    try {
      setPaymentOverlayOpen(true);
      const order = await runCheckout({
        store,
        customer: draftCustomer,
        cartItems,
        userEmail,
        addresses,
      });

      clearCart(store.id);
      resetCheckoutContext();
      void refreshAllOrders();
      await payOrder(order);
      if (store.id === selectedStore?.id) {
        await refreshCustomer();
      }
    } catch {
      // erro mapeado no hook
    } finally {
      setPaymentOverlayOpen(false);
    }
  }, [
    checkoutDraft,
    activeCheckoutStore,
    activeCheckoutCustomer,
    userEmail,
    runCheckout,
    clearCart,
    resetCheckoutContext,
    refreshAllOrders,
    payOrder,
    refreshCustomer,
    selectedStore?.id,
  ]);

  const beginCheckoutForStore = useCallback(
    async (targetStoreId: string) => {
      if (!canBrowseStores) {
        blockStoreBrowsing();
        return;
      }
      const targetStore = stores.find((store) => store.id === targetStoreId);
      if (!targetStore) return;

      const targetCartItems = getCartItems(targetStoreId);
      if (targetCartItems.length === 0) {
        toast.warning("Adicione produtos ao pedido antes de finalizar.");
        return;
      }

      setCheckoutPreparing(true);

      try {
        let targetCustomer: WooCustomer | null =
          targetStoreId === selectedStore?.id && customerStatus === "found"
            ? customer
            : null;

        if (!targetCustomer) {
          targetCustomer = await woocommerceStoreService.findCustomerByEmail(
            targetStore,
            userEmail,
          );
        }

        if (!targetCustomer) {
          toast.error(
            "Conta não encontrada neste dispensador. Faça login primeiro.",
          );
          return;
        }

        let storeOrders = orders.filter((order) => order.storeId === targetStoreId);
        if (storeOrders.length === 0) {
          try {
            storeOrders = await woocommerceStoreService.listRecentOrdersForCheckout(
              targetStore,
              targetCustomer.id,
            );
          } catch {
            storeOrders = [];
          }
        }

        const prefilledAddresses = buildDefaultCheckoutAddresses({
          orders: storeOrders,
          userPatient,
          userEmail,
          userName,
          patientCpf: patientId,
          customer: targetCustomer,
        });

        const addressOptions = buildAddressOptionsForCheckout({
          orders: storeOrders,
          userPatient,
          userEmail,
          userName,
          patientCpf: patientId,
        });
        setCheckoutAddressOptions(addressOptions);

        setCheckoutDraft({
          store: targetStore,
          customer: targetCustomer,
          cartItems: targetCartItems,
          addresses: prefilledAddresses,
        });
        setShowCheckoutReview(true);
      } catch {
        toast.error("Não foi possível verificar sua conta no dispensador.");
      } finally {
        setCheckoutPreparing(false);
      }
    },
    [
      stores,
      getCartItems,
      selectedStore?.id,
      customer,
      customerStatus,
      userEmail,
      userPatient,
      userName,
      orders,
      patientId,
      canBrowseStores,
      blockStoreBrowsing,
    ],
  );

  const handleQuickCheckout = useCallback(() => {
    if (!canBrowseStores) {
      blockStoreBrowsing();
      return;
    }
    if (totalItems <= 0) return;

    if (selectedStore && getCartItems(selectedStore.id).length > 0) {
      void beginCheckoutForStore(selectedStore.id);
      return;
    }

    const storeWithItems = stores.find((store) => getCartItems(store.id).length > 0);
    if (storeWithItems) {
      void beginCheckoutForStore(storeWithItems.id);
    }
  }, [canBrowseStores, blockStoreBrowsing, totalItems, selectedStore, getCartItems, stores, beginCheckoutForStore]);

  const handleOpenOrders = useCallback(
    (tab: OrdersTab = "unpaid") => {
      void refreshAllOrders();
      setOrdersModalTab(tab);
      setOrdersModalOpen(true);
    },
    [refreshAllOrders],
  );

  const handleAddressWizardComplete = useCallback(
    (addresses: NonNullable<MarketplaceCheckoutDraft["addresses"]>) => {
      setCheckoutDraft((prev) => (prev ? { ...prev, addresses } : prev));
      setShowAddressWizard(false);
      setShowCheckoutReview(true);
    },
    [],
  );

  const headerActions = useMemo(
    () => (
      <WooOrdersTriggerButton
        showLabel
        unpaidCount={pendingOrders.length}
        loading={ordersLoading}
        onOpenOrders={() => handleOpenOrders("unpaid")}
      />
    ),
    [pendingOrders.length, ordersLoading, handleOpenOrders],
  );

  useEffect(() => {
    if (!onHeaderActionsChange) return;
    if (!isConfigured) {
      onHeaderActionsChange(null);
      return;
    }
    onHeaderActionsChange(headerActions);
    return () => onHeaderActionsChange(null);
  }, [headerActions, onHeaderActionsChange, isConfigured]);

  const handleCancelOrder = useCallback(
    (order: WooOrder) => {
      void cancelOrder(order);
    },
    [cancelOrder],
  );

  if (storesLoading) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography sx={{ ...mt.body, color: "text.secondary" }}>
          Carregando dispensadores...
        </Typography>
      </Box>
    );
  }

  if (storesConfigError || storesError) {
    return (
      <MarketplaceConfigError
        message={storesConfigError ?? storesError ?? "Erro ao carregar dispensadores."}
      />
    );
  }

  if (!isConfigured) {
    const misconfigured = stores.find((store) => store.integrationError);
    return (
      <MarketplaceConfigError
        message={
          misconfigured?.integrationError ??
          "Nenhum dispensador com integração WooCommerce ativa. Configure VITE_WOO_ORGANIZATION_STORES com siteOrigin por CNPJ."
        }
      />
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        mt: inline && !hideHeader ? 2 : 0,
        p: hideHeader ? { xs: 0, md: 0 } : { xs: 1.5, md: 2 },
        borderRadius: hideHeader ? 0 : 2,
        border: hideHeader
          ? "none"
          : `1px solid ${alpha(theme.palette.primary800.main, 0.14)}`,
        bgcolor: hideHeader
          ? "transparent"
          : alpha(theme.palette.primary800.main, 0.02),
        overflow: "hidden",
      }}
    >
      {!hideHeader ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box>
            <Typography sx={{ ...mt.pageTitle, color: "primary800.main" }}>
              Dispensação
            </Typography>
            <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
              Escolha um dispensador para solicitar produtos canabinoides.
            </Typography>
          </Box>
        </Stack>
      ) : null}

      {!canBrowseStores ? (
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha(theme.palette.warning.main, 0.28),
            bgcolor: alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <Stack spacing={1.25} alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <DescriptionOutlinedIcon color="warning" fontSize="small" />
              <Typography sx={{ ...mt.sectionTitle, color: "warning.dark" }}>
                Documentos pendentes
              </Typography>
            </Stack>
            <Typography sx={{ ...mt.body, color: "text.secondary" }}>
              {documentsPendingCount > 0
                ? `Envie ${documentsPendingCount} documento(s) restante(s) na seção Documentos para acessar os dispensadores.`
                : "Envie todos os documentos do tratamento canabinoide para acessar os dispensadores."}
            </Typography>
            <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
              Você ainda pode acompanhar e pagar pedidos já criados em Meus pedidos.
            </Typography>
          </Stack>
        </Box>
      ) : !selectedStore ? (
        <Box sx={{ maxHeight: { xs: 360, md: 420 }, overflowY: "auto", pr: 0.5 }}>
          <CannabinoidStorePicker
            stores={stores}
            selectedStoreId={null}
            onSelectStore={(store) => void handleSelectStore(store)}
            consentedInstitutionIds={consentedInstitutionIds}
            variant="grid"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: { md: "calc(100vh - 280px)" },
            minHeight: { xs: 480, md: 520 },
            maxHeight: { md: "70vh" },
            overflow: "hidden",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: { md: 220 },
              flexShrink: 0,
              overflowY: "auto",
              display: { xs: "none", md: "block" },
              pr: 0.5,
            }}
          >
            <Typography
              sx={{ ...mt.sectionTitle, color: "primary800.main", display: "block", mb: 1 }}
            >
              Dispensadores
            </Typography>
            <CannabinoidStorePicker
              stores={stores}
              selectedStoreId={selectedStore.id}
              onSelectStore={(store) => void handleSelectStore(store)}
              consentedInstitutionIds={consentedInstitutionIds}
              variant="list"
            />
          </Box>

          <Stack sx={{ flex: 1, minWidth: 0, overflow: "hidden", position: "relative" }}>
            <Box sx={{ display: { xs: "flex", md: "none" }, mb: 1 }}>
              <SecondaryButton
                width="100%"
                height="44px"
                onClick={() => setSelectedStore(null)}
              >
                Trocar dispensador
              </SecondaryButton>
            </Box>

            <Typography sx={{ ...mt.sectionTitle, mb: 1 }}>{selectedStore.name}</Typography>

            {customerStatus === "loading" || customerLoading ? (
              <Typography sx={{ ...mt.body, color: "text.secondary" }}>
                Verificando sua conta no dispensador...
              </Typography>
            ) : null}

            {canBrowseStoreCatalog ? (
              <>
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: alpha(theme.palette.primary800.main, 0.02),
                    pb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "stretch",
                      gap: { xs: 1, sm: 0 },
                      borderRadius: 1.5,
                      border: {
                        xs: "none",
                        sm: `1px solid ${alpha(theme.palette.primary800.main, 0.2)}`,
                      },
                      bgcolor: { xs: "transparent", sm: "background.paper" },
                      overflow: "hidden",
                      boxShadow: {
                        xs: "none",
                        sm: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
                      },
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Buscar produtos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: { xs: 1.5, sm: 0 },
                        border: {
                          xs: `1px solid ${alpha(theme.palette.primary800.main, 0.2)}`,
                          sm: "none",
                        },
                        "& .MuiOutlinedInput-root": {
                          ...mt.input,
                          height: 44,
                          borderRadius: { xs: 1.5, sm: 0 },
                          "& fieldset": { border: "none" },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <WooCheckoutTriggerButton
                      draftItemCount={totalItems}
                      loading={checkoutLoading || checkoutPreparing}
                      onCheckout={handleQuickCheckout}
                      attached={!isMobileLayout}
                    />
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, pb: 1 }}>
                  <WooProductGrid
                    products={products}
                    loading={productsLoading}
                    error={productsError}
                    onAddToCart={handleAddToCart}
                    getItemQuantity={(productId) => getItemQuantity(storeId, productId)}
                    onUpdateQuantity={(productId, qty) =>
                      updateQuantity(storeId, productId, qty)
                    }
                  />
                </Box>
              </>
            ) : null}
          </Stack>
        </Box>
      )}

      <WooOrdersModal
        open={ordersModalOpen}
        onClose={() => setOrdersModalOpen(false)}
        initialTab={ordersModalTab}
        orders={orders}
        stores={stores}
        loading={ordersLoading}
        monitoringOrderId={monitoringOrderId}
        onCancelOrder={handleCancelOrder}
        onPayOrder={payOrder}
        onExploreProducts={() => setOrdersModalOpen(false)}
      />

      <CheckoutPaymentOverlay
        open={
          (paymentOverlayOpen || monitoringOrderId !== null) &&
          pixPayment === null &&
          embeddedPayment === null
        }
        orderId={monitoringOrderId}
        preparing={paymentOverlayOpen && monitoringOrderId === null}
      />

      <MarketplacePaymentIframeOverlay
        open={embeddedPayment !== null}
        paymentUrl={embeddedPayment?.url ?? ""}
        storeName={embeddedPayment?.storeName}
        onBack={closeEmbeddedPayment}
      />

      <PixPaymentDialog
        open={pixPayment !== null}
        order={pixPayment?.order ?? null}
        pixDetails={pixPayment?.details ?? null}
        monitoring={monitoringOrderId !== null}
        onClose={closePixPayment}
      />

      <WooPaymentSuccessDialog
        open={Boolean(successOrder)}
        order={successOrder}
        onClose={clearSuccessOrder}
        onViewOrders={() => {
          clearSuccessOrder();
          handleOpenOrders("paid");
        }}
      />

      <MarketplaceWaitingOverlay
        open={isLinkingAccount}
        title="Vinculando conta"
        subtitle="Conclua o login na janela ou aba que abriu. Verificamos a vinculação automaticamente a cada poucos segundos."
        detail={selectedStore?.name}
        onCancel={cancelLinkAccount}
      />

      <WooLoginPromptDialog
        open={showLoginPrompt}
        storeName={selectedStore?.name ?? "dispensador"}
        onClose={() => {
          setSelectedStore(null);
          resetCustomer();
        }}
        onLogin={() => {
          void linkAccount()
            .then((result) => {
              if (result === "cancelled" || result) return;
              toast.info(
                "Não foi possível confirmar a vinculação. Tente novamente.",
              );
            })
            .catch((err: unknown) => {
              toast.error(
                err instanceof Error
                  ? err.message
                  : "Não foi possível abrir a vinculação de conta.",
              );
            });
        }}
      />

      {checkoutDraft && activeCheckoutCustomer ? (
        <>
          <CheckoutReviewDialog
            open={showCheckoutReview}
            draft={checkoutDraft}
            loading={checkoutLoading}
            onClose={resetCheckoutContext}
            onEditProducts={() => {
              setShowCheckoutReview(false);
            }}
            onEditShipping={() => {
              setShowCheckoutReview(false);
              setAddressWizardStep(1);
              setAddressWizardFocus("shipping");
              setShowAddressWizard(true);
            }}
            onEditBilling={() => {
              setShowCheckoutReview(false);
              setAddressWizardStep(2);
              setAddressWizardFocus("billing");
              setShowAddressWizard(true);
            }}
            onConfirm={() => void executeCheckout()}
          />

          <CheckoutAddressWizardDialog
            open={showAddressWizard}
            initialStep={addressWizardStep}
            wizardFocus={addressWizardFocus}
            customer={activeCheckoutCustomer}
            userEmail={userEmail}
            userPatient={userPatient}
            userName={userName}
            patientCpf={patientId}
            addressOptions={checkoutAddressOptions}
            initialShipping={checkoutDraft.addresses?.shipping}
            initialBilling={checkoutDraft.addresses?.billing}
            onClose={() => {
              setShowAddressWizard(false);
              setShowCheckoutReview(true);
            }}
            onComplete={handleAddressWizardComplete}
          />
        </>
      ) : null}

      {consentDialogStore?.institutionId ? (
        <DispensaryConsentDialog
          open
          patientId={patientId}
          dispensaryName={consentDialogStore.name}
          institutionId={consentDialogStore.institutionId}
          onClose={() => setConsentDialogStore(null)}
          onGranted={handleConsentGranted}
        />
      ) : null}
    </Stack>
  );
}
